import { Component, Vue } from "vue-property-decorator";
import Broadcaster, {
  BroadcastersCatalog,
  BroadcastersCollection,
} from "@/models/SpecialInitiatives/Broadcaster";

type Kind = "TV" | "RADIO";

export interface SpecialInitiativesColumn {
  value: string;
  label: string;
  isHighlight?: boolean;
}

export interface SpecialInitiativesRow {
  subject: string;
  broadcaster?: string;
  kind: Kind | "";
  data: { [key: string]: { value: number; quote: number } };
  children: SpecialInitiativesRow[];
  isTotal: boolean;
  logoUrls?: string[];
}
export type CollectionData = {
  col: string;
  value: number;
  quote: number;
  logoUrls?: string[];
};
export type SubjectCollection = { [key: string]: CollectionData[] };
export type ParentRecord = {
  data: CollectionData[];
  subjects: SubjectCollection;
  mediaType: Kind;
};
export type ParentCollection = { [key: string]: ParentRecord };

export type SubjectColumn = "sales_agencies" | "editors" | "market_segments";
export interface SpecialInitiativesIColumn {
  name: string;
  label: string;
}

@Component
export default class TrendMixin extends Vue {
  sortColumn = "";
  sortDirection: "ASC" | "DESC" | null = null;
  columns: SpecialInitiativesColumn[] = [];
  rows: SpecialInitiativesRow[] = [];

  buildSpecialInitiativesColumnsAndRows(
    loading: boolean,
    subject: SubjectColumn,
    periods: SpecialInitiativesIColumn[],
    exclusivity: string,
    unitOfMeasurement: string,
    broadcasters: BroadcastersCatalog
  ) {
    this.columns = this.loadColumns(loading, subject, periods);
    this.convertValueToRows(
      exclusivity,
      unitOfMeasurement,
      broadcasters,
      subject,
      periods
    );
  }

  loadColumns(
    loading: boolean,
    subject: SubjectColumn,
    periods: SpecialInitiativesIColumn[]
  ): SpecialInitiativesColumn[] {
    if (loading) {
      return [];
    }
    const columns: SpecialInitiativesColumn[] = [];
    switch (subject) {
      case "sales_agencies":
        columns.push({
          value: "subject",
          label: "Concessionarie",
          isHighlight: false,
        });
        break;
      case "editors":
        columns.push({
          value: "subject",
          label: "Editori",
          isHighlight: false,
        });
        break;
      case "market_segments":
        columns.push({
          value: "subject",
          label: "Segmenti di mercato",
          isHighlight: false,
        });
        break;
    }
    periods.forEach((period) => {
      columns.push({
        value: period.name,
        label: period.label,
        isHighlight: false,
      });
    });
    return columns;
  }

  convertValueToRows(
    exclusivity: string,
    unitOfMeasurement: string,
    broadcasters: BroadcastersCatalog,
    subject: SubjectColumn,
    periods: SpecialInitiativesIColumn[]
  ) {
    const parents = this.convertBroadcastersToParentsCollection(
      exclusivity,
      unitOfMeasurement,
      subject,
      broadcasters
    );
    const tvTotals: CollectionData[] = [];
    const radioTotals: CollectionData[] = [];
    const totals = this.computeParentTotals(parents, tvTotals, radioTotals);
    this.convertParentsToRow(parents, totals, tvTotals, radioTotals);
    this.computeRowQuotes(this.rows, periods);
  }

  private convertBroadcastersToParentsCollection(
    exclusivity: string,
    unitOfMeasurement: string,
    subject: SubjectColumn,
    broadcasters: BroadcastersCatalog
  ): ParentCollection {
    const parents: ParentCollection = {};
    const records = this.broadcastersToTableData(exclusivity, broadcasters);
    for (const record of records) {
      const key = this.detectionKey(subject, record);
      const parent = parents[key] || {
        data: [],
        subjects: {},
        mediaType: record.mediaType,
      };
      const parentSubject = parent.subjects[record.brandName] || [];
      this.updateSubjectAndParentData(
        parentSubject,
        parent,
        record,
        `col-${record.timeGranularity}`,
        unitOfMeasurement
      );
      parent.subjects[record.brandName] = parentSubject;
      parents[key] = parent;
    }
    return parents;
  }

  broadcastersToTableData(
    exclusivity: string,
    broadcasters: BroadcastersCatalog
  ): Broadcaster[] {
    for (const weekRange in broadcasters) {
      return this.broadcastersFromExclusivity(
        exclusivity,
        broadcasters[weekRange]
      );
    }
    return [];
  }

  broadcastersFromExclusivity(
    exclusivity: string | null,
    broadcasters: BroadcastersCollection
  ) {
    if (exclusivity === "all") {
      return broadcasters.broadcastersAll;
    } else if (exclusivity === "my_exclusivity_type") {
      return broadcasters.broadcastersMine;
    } else if (exclusivity === "others_exclusivity_type") {
      return broadcasters.broadcastersOthers;
    } else if (exclusivity === "shared_exclusivity_type") {
      return broadcasters.broadcastersShared;
    } else {
      return [];
    }
  }

  private detectionKey(subject: SubjectColumn, record: Broadcaster): string {
    if (subject === "sales_agencies") {
      return `${record.currentAgency}`;
    } else if (subject === "editors") {
      return `${record.publisher}`;
    }
    return `${record.marketSegment}`;
  }

  private computeRowQuotes(
    rows: SpecialInitiativesRow[],
    periods: SpecialInitiativesIColumn[]
  ) {
    rows.forEach((row) => {
      periods.forEach((col, index) => {
        if (index > 0) {
          if (!row.data[col.name]) {
            return;
          }
          const currentValue = row.data[col.name].value;
          const previousValue = row.data[periods[index - 1].name].value;
          if (previousValue === 0) {
            row.data[col.name].quote = 0;
          } else {
            row.data[col.name].quote = Number(
              (currentValue / previousValue - 1) * 100
            );
          }
        }
      });
      this.computeRowQuotes(row.children, periods);
    });
  }

  private updateSubjectAndParentData(
    subject: CollectionData[],
    parent: ParentRecord,
    record: Broadcaster,
    col: string,
    unitOfMeasurement: string
  ) {
    let value = 0;
    if (
      unitOfMeasurement === "seconds" ||
      unitOfMeasurement === "quoted_seconds"
    ) {
      value = record.secondsCount;
    }
    if (unitOfMeasurement === "spots" || unitOfMeasurement === "quoted_spots") {
      value = record.detectionsCount;
    }
    const data = {
      col,
      value,
      quote: 0,
      logoUrls: record.logoUrls,
    };
    subject.push(data);
    parent.data.push(data);
  }

  private computeParentTotals(
    parents: ParentCollection,
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ): CollectionData[] {
    const totals: CollectionData[] = [];
    for (const subject in parents) {
      const parent = parents[subject];
      parent.data = parent.data.reduce(
        (acc: CollectionData[], element: CollectionData) => {
          this.updateTotal(acc, element);
          if (parent.mediaType === "TV") {
            this.updateTotal(tvTotals, element);
          } else if (parent.mediaType === "RADIO") {
            this.updateTotal(radioTotals, element);
          }
          this.updateTotal(totals, element);
          return acc;
        },
        []
      );
    }
    return totals;
  }

  private updateTotal(totals: CollectionData[], element: CollectionData) {
    const total = totals.find((e) => e.col == element.col);
    if (!total) {
      totals.push(Object.assign({}, element));
    } else {
      total.value += element.value;
    }
  }

  private convertParentsToRow(
    parents: ParentCollection,
    totals: CollectionData[],
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ) {
    this.rows = [];
    for (const subject in parents) {
      const parent = parents[subject];
      const rowData: { [key: string]: { value: number; quote: number } } = {};
      this.dataColumns().forEach((column) => {
        const period = column.value;
        const element = parent.data.find((e) => e.col === period) || {
          period,
          value: 0,
          quote: 0,
        };
        rowData[column.value] = {
          value: element.value,
          quote: 0,
        };
      });
      const row: SpecialInitiativesRow = {
        subject,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: this.convertChildrenToRows(parent),
      };
      this.rows.push(row);
    }
    if (tvTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(tvTotals, "TOTALE TV", "TV", true)
      );
    }
    if (radioTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(radioTotals, "TOTALE RADIO", "RADIO", true)
      );
    }
    this.rows.push(this.CollectionDataListToRow(totals, "TOTALE", "", true));
    this.rows.sort(this.sort.bind(this));
  }

  sort(a: SpecialInitiativesRow, b: SpecialInitiativesRow): -1 | 0 | 1 {
    if (this.sortDirection !== null) {
      return this.baseSort(
        a,
        b,
        this.sortByColumn(a, b, this.sortColumn, this.sortDirection)
      );
    }
    return this.baseSort(a, b);
  }

  private baseSort(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    otherCondition?: (
      a: SpecialInitiativesRow,
      b: SpecialInitiativesRow
    ) => -1 | 0 | 1
  ): -1 | 0 | 1 {
    let result: -1 | 0 | 1 = 0;
    if (a.isTotal && a.kind == "") {
      return 1;
    }
    if (b.isTotal && b.kind == "") {
      return -1;
    }
    if (a.kind == "TV" && b.kind == "RADIO") {
      result = -1;
    } else if (a.kind == "RADIO" && b.kind == "TV") {
      result = 1;
    }
    if (a.kind == b.kind) {
      if (a.isTotal) {
        result = 1;
      } else if (b.isTotal) {
        result = -1;
      } else {
        if (otherCondition) {
          result = otherCondition(a, b);
          a.children.sort(otherCondition);
          b.children.sort(otherCondition);
        }
      }
    }
    return result;
  }

  private sortByColumn(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    columnName: string,
    direction: "ASC" | "DESC"
  ): (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => -1 | 0 | 1 {
    return (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => {
      let result: -1 | 0 | 1 = 0;
      if (columnName == "subject") {
        if (a.subject < b.subject) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.subject < a.subject) {
          result = direction === "ASC" ? 1 : -1;
        }
      } else {
        if (a.data[columnName] === undefined) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.data[columnName] === undefined) {
          result = direction === "ASC" ? 1 : -1;
        } else if (a.data[columnName].value < b.data[columnName].value) {
          result = direction === "ASC" ? -1 : 1;
        } else {
          result = direction === "ASC" ? 1 : -1;
        }
      }
      return result;
    };
  }

  private CollectionDataListToRow(
    list: CollectionData[],
    name: string,
    kind: Kind | "",
    isTotal: boolean
  ) {
    const rowData: { [key: string]: { value: number; quote: number } } = {};
    this.dataColumns().forEach((column) => {
      const col = column.value;
      const element = list.find((e) => e.col === col) || {
        col,
        value: 0,
        quote: 0,
      };
      rowData[column.value] = {
        value: element.value,
        quote: 0,
      };
    });
    const row: SpecialInitiativesRow = {
      subject: name,
      data: rowData,
      isTotal,
      children: [],
      kind,
    };
    return row;
  }

  private convertChildrenToRows(parent: ParentRecord): SpecialInitiativesRow[] {
    const rows: SpecialInitiativesRow[] = [];
    for (const key in parent.subjects) {
      const subject = parent.subjects[key];
      const rowData: { [key: string]: { value: number; quote: number } } = {};
      this.dataColumns().forEach((column) => {
        const period = column.value;
        const element = subject
          .filter((e) => e.col === period)
          .reduce(
            (acc, element) => {
              acc.period = element.col;
              acc.value += element.value;
              return acc;
            },
            { period, value: 0, quote: 0 }
          );
        rowData[column.value] = {
          value: element.value,
          quote: 0,
        };
      });
      const row: SpecialInitiativesRow = {
        subject: key,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: [],
        logoUrls: subject[0].logoUrls,
      };
      rows.push(row);
    }
    return rows;
  }

  dataColumn(columnName: string) {
    return columnName.startsWith("col");
  }

  dataColumns() {
    return this.columns.filter((c) => this.dataColumn(c.value));
  }
}
